import React, { FC } from "react";
import { GroupListItem } from "@themuse/design-system/lib/components/GroupList/GroupList";
import { useSearchState } from "hooks/SearchContext";
import { FilterPanel } from "./FilterPanel";
import { IconArrow } from "../icons/IconArrow";
import styles from "./FilterTrigger.module.scss";
import { ToggleSwitch } from "../icons/ToggleSwitch";

export interface FilterTriggerProps {
    id: string;
    label: string;
    options: GroupListItem[]
    hasIcon?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    onCancel?: () => void;
    onBackBtn?: () => void;
    isOpen?: boolean;
    isMegaMenu: boolean;
    wrapperClass?: string;
}

/**
 * `FilterTrigger` is a functional component that renders a filter button with optional
 * sub-menu functionality. It displays the count of active filters and provides a toggle
 * for remote filter. The component can be customized with icons and
 * different styles based on its props.
 */
export const FilterTrigger: FC<FilterTriggerProps> = ({
    id,
    label,
    options,
    hasIcon = false,
    disabled = false,
    onClick = () => { },
    onCancel = () => { },
    onBackBtn = () => { },
    isOpen = false,
    isMegaMenu = false,
    wrapperClass = ""
}) => {
    const {
        location: { userSelectedLocation },
        filters
    } = useSearchState();

    // Determines the count of active filters for a specific filter type.
    const singleFilterCount = () => {
        if (id === "remote") {
            return userSelectedLocation?.remote_work_location_type ? 1 : 0;
        }
        if (id === "postedDateRange") {
            return filters.postedDateRange ? 1 : 0;
        }
        if (id === "radius") {
            return filters.radius ? 1 : 0;
        }
        return filters[id].filter(Boolean).length;
    };

    // Count to display on the button..
    const btnCounter = singleFilterCount();

    // Submit the items to the backend and fire the supplied onclick event.
    const onApply = () => {
        onClick();
    };

    const btnClass = `${styles.FilterPanelBase} ${btnCounter ? styles.active : ""}`;
    const hasSubMenu = !(isMegaMenu || id === "remote" || disabled);

    return (
        <div className={`${styles.filterBarDropdowns} ${wrapperClass}`}>
            <button
                className={btnClass}
                type="button"
                tabIndex={0}
                aria-label={`Filter by ${label}`}
                onClick={onClick}
                disabled={disabled}
            >
                <div className={styles.inputWrapper}>{label}</div>

                {id !== "remote" && btnCounter
                    ? (<div className={styles.filter} aria-label={`${btnCounter} selected`}>{btnCounter}</div>)
                    : null}

                {id === "remote" && isMegaMenu
                    ? (<ToggleSwitch {...{ isOn: !!btnCounter, onToggle: onClick }} />)
                    : null}

                {hasIcon
                    ? (
                        <div className={styles.icons}>
                            <IconArrow className={`${styles.img} ${isOpen ? styles.open : ""}`} />
                        </div>
                    )
                    : null}
            </button>

            {hasSubMenu
                ? (
                    <FilterPanel {...{
                        filterID: id, label, isOpen, options, onCancel, onApply, onBackBtn
                    }}
                    />
                )
                : null}
        </div>
    );
};
